import React, { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import NoConnectionsIcon from "../../assets/icons/NoConnections.svg";
import { ArrowIcon } from "../../front-end-global-components/assets/assets";
import consentAgreement from "../../front-end-global-components/assets/ConsentAgreement.svg";
import filterGrey from "../../front-end-global-components/assets/filterGrey.svg";
import ConditionalRender from "../../front-end-global-components/components/ConditionalRender/ConditionalRender";
import Modal from "../../front-end-global-components/components/Modal/Modal";
import RoundedProfilePicture from "../../front-end-global-components/components/RoundedProfilePicture/RoundedProfilePicture";
import { consentStatus } from "../../utils/constants";
import { isValidObject } from "../../utils/validators";
import { StatusChip } from "../Chip/Chip";
import "./Connections.css";

const BACKGROUND_REFRESH_INTERVAL = 10000;

export default function Connections(props) {
  // full fetch on mount and whenever the profile changes
  useEffect(() => {
    if (typeof props.fetchAbdmRequests === "function") {
      props.fetchAbdmRequests();
    }
    // eslint-disable-next-line
  }, [props.profileId]);

  // consent requests live on the ABDM gateway (no listener), so poll while the
  // list is on screen and refresh when the app comes back into view
  useEffect(() => {
    if (typeof props.fetchAbdmRequests !== "function") return;

    const silentRefresh = () => {
      if (!document.hidden) {
        props.fetchAbdmRequests({ silent: true });
      }
    };

    const interval = setInterval(silentRefresh, BACKGROUND_REFRESH_INTERVAL);
    document.addEventListener("visibilitychange", silentRefresh);
    window.addEventListener("focus", silentRefresh);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", silentRefresh);
      window.removeEventListener("focus", silentRefresh);
    };
    // eslint-disable-next-line
  }, [props.profileId]);

  const navigate = useNavigate();

  return (
    <CSSTransition
      in={props.show}
      timeout={2000}
      classNames="fade-in-fade-out-transition"
    >
      <ConditionalRender condition={!!props.isHealthIdLoggedIn}>
        {props.isUserAgreed ? (
          <ABDMRequests
            loading={props.loading}
            requests={props.requests}
            abdmRequestOnClick={props.abdmRequestOnClick}
            autoApprovalPolicies={props.autoApprovalPolicies}
            setRequestsFilter={(filterBy, id) => {
              props.setRequestsFilter(filterBy, id);
            }}
            filters={props.filters}
          />
        ) : (
          <div className=" inherit-parent-height overflow-y-auto flex-place-children-page-center padding-large">
            <img src={consentAgreement} alt="Consent Agreement" />
            <div className=" padding-top-large font-family-gilroy-medium font-color-secondary font-size-large ">
              Consent agreement
            </div>
            <div className="font-color-tertiary font-size-large text-align-center padding-top-default ">
              To access all features, agree our{" "}
              <span
                className="font-color-primary cursor-pointer"
                onClick={() => {
                  navigate("/");
                }}
              >
                consent form{" "}
              </span>{" "}
              on the home screen.
            </div>
          </div>
        )}
      </ConditionalRender>
    </CSSTransition>
  );
}

const ABDMRequests = (props) => {
  const navigate = useNavigate();

  const [abdmRequests, setAbdmRequests] = useState([]);
  const [clinicNames, setClinicNames] = useState([]);

  useEffect(() => {
    const combinedList = props.requests && Object.values(props.requests).flat();

    combinedList?.sort(
      (first, second) =>
        +new Date(second.lastUpdated) - +new Date(first.lastUpdated)
    );

    setAbdmRequests(combinedList);
  }, [props.requests]);

  useEffect(() => {
    let _clinicNames = {};

    abdmRequests?.forEach((data) => {
      _clinicNames = { ..._clinicNames, [data?.hiu?.id]: data?.hiu };
    });

    if (isValidObject(_clinicNames)) {
      setClinicNames(_clinicNames);
    }
  }, [abdmRequests]);

  const filteredRequests =
    abdmRequests?.filter((request) => {
      return (
        (props.filters.clinics.includes(request?.hiu?.id) ||
          props.filters.clinics.length === 0) &&
        (props.filters.requests.includes(request.type) ||
          props.filters.requests.length === 0) &&
        (props.filters.status.includes(request.status) ||
          props.filters.status.length === 0)
      );
    }) || [];

  const isFilterApplied =
    props.filters.clinics.length +
      props.filters.requests.length +
      props.filters.status.length >
    0;

  return (
    <>
      {props.loading && <ABDMRequestsCardLoading />}

      {!props.loading && abdmRequests?.length !== 0 && (
        <div className="position-sticky top-0 background-white z-index-1 inherit-parent-width flex-justify-content-space-between flex-align-items-center font-size-medium font-color-secondary padding-top-large padding-bottom-default padding-right-large padding-left-large">
          <div>CONNECTIONS & REQUESTS</div>
          <div
            className="font-family-gilroy-medium display-flex flex-align-items-center cursor-pointer"
            onClick={() => navigate("filter")}
            data-cy="FILTER-ICON"
          >
            <div>
              Filters
              {isFilterApplied &&
                ` (${
                  props.filters.clinics.length +
                  props.filters.requests.length +
                  props.filters.status.length
                })`}
            </div>
            <img
              src={filterGrey}
              className="padding-left-default"
              height="12px"
              alt="filter-icon"
            />
          </div>
        </div>
      )}

      {!props.loading &&
        (filteredRequests.length === 0 || abdmRequests === 0) && (
          <FallBack isFilter={isFilterApplied} />
        )}

      {!props.loading && filteredRequests.length > 0 && (
        <section className="settings-screen-content-remaining-height overflow-y-auto">
          {filteredRequests.map((request, index) => (
            <React.Fragment key={`${request.id}/${index}`}>
              <AbdmRequestCard
                onClick={props.abdmRequestOnClick}
                status={request.status}
                loading={props.loading}
                data={request}
              />
            </React.Fragment>
          ))}
        </section>
      )}

      <Routes>
        <Route
          path="/filter"
          element={
            <FilterModal
              clinicNames={clinicNames}
              filters={props.filters}
              setRequestsFilter={props.setRequestsFilter}
            />
          }
        />
      </Routes>
    </>
  );
};

function remainingDateConversion(date) {
  const timeStamp = +new Date(date) - +new Date();
  const remainingMonth = Math.round(timeStamp / (1000 * 60 * 60 * 24 * 30));
  const remainingDays = Math.round(timeStamp / (1000 * 60 * 60 * 24));

  return remainingMonth < 1
    ? `Expires in ${remainingDays} Day${remainingDays === 1 ? "" : "s"}`
    : `Expires in ${remainingMonth} Month${remainingMonth === 1 ? "" : "s"}`;
}

const AbdmRequestCard = (props) => {
  const [requester, setRequester] = useState({
    name: "",
    suffixText: "",
    expiresIn: ""
  });

  useEffect(() => {
    switch (props.data.type) {
      case "subscription":
        setRequester({
          name: props.data?.hiu?.name || "Abdm",
          suffixText:
            props.data?.status === consentStatus.pending
              ? "wants to follow and store your health record when new record is available in your ABHA address."
              : "Subscription request"
        });
        break;

      case "consent":
        setRequester({
          name: props.data?.requester?.name || "Abdm",
          suffixText:
            props.data?.status === consentStatus.pending
              ? "want to access select records in your ABHA address."
              : "Consent request",
          expiresIn:
            new Date(props.data?.permission?.dataEraseAt) > new Date() &&
            props.data?.status === consentStatus.approved &&
            remainingDateConversion(props.data?.permission?.dataEraseAt)
        });
        break;
      default:
        break;
    }
  }, [props.data, props.status]);

  return (
    <div
      data-cy={`${props.data.type}-${props.status}`}
      id={`${props.data.type}-${props.status}-${props.data?.hiu?.id}`}
      className="margin-top-medium margin-bottom-medium margin-left-large margin-right-large flex-justify-content-space-between"
    >
      <div
        onClick={() => {
          if (props.loading === false) {
            props.onClick(props.data.type, props.data.requestId);
          }
        }}
        className={` display-flex flex-grow-1  
   }`}
      >
        <RoundedProfilePicture
          loading={props.loading}
          className={`${props.loading ? "" : "margin-top-default"}`}
          size={"medium"}
          src={""}
        />
        <div>
          <div className="flex-grow-1 padding-left-medium padding-top-default font-color-secondary">
            <div className="font-family-gilroy-medium">{requester.name}</div>
            <div className="font-family-gilroy-regular">
              {requester.suffixText}
              {requester.expiresIn && (
                <div className="font-size-small padding-top-small">
                  {requester.expiresIn}
                </div>
              )}
            </div>
          </div>
          <div className="padding-left-medium padding-top-small">
            <StatusChip status={props.status} />
          </div>
        </div>
      </div>
      <div className="margin-top-default">
        <ArrowIcon />
      </div>
    </div>
  );
};

const ABDMRequestsCardLoading = () => {
  return (
    <>
      {[...Array(8)].map((_data, index) => (
        <div
          key={index}
          className="padding-top-default padding-left-large padding-right-large"
        >
          <div className="flex-center-children-vertically padding-top-large">
            <div className="margin-top-large shimmer padding-larger border-radius-50-percentage height-width-16px" />
            <div className="margin-left-default">
              <div className="shimmer padding-default border-radius-default width-200px" />
              <div className="shimmer margin-top-default padding-small border-radius-default width-150px ">
                <div className="padding-smaller" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

const FilterModal = (props) => {
  const navigate = useNavigate();

  const requestTypes = [
    // { id: "authorization", name: "Authorization request" },
    { id: "consent", name: "Consent request" },
    { id: "subscription", name: "Subscription request" }
    // { id: "lockerSetup", name: "Locker request" }
  ];

  const statusTypes = [
    { id: consentStatus.approved, name: "Approved" },
    { id: consentStatus.autoApproved, name: "Auto-approved" },
    { id: consentStatus.denied, name: "Denied" },
    { id: consentStatus.expired, name: "Expired" },
    { id: consentStatus.pending, name: "Pending" },
    { id: consentStatus.revoked, name: "Revoked" }
  ];

  return (
    <Modal
      onClose={() => {
        navigate(-1);
      }}
      canIgnore={true}
      show={true}
      position="position-fixed bottom-0 left-0 right-0"
      boxShadow="none"
      borderRadius="none"
      width="inherit-parent-width"
      background="background-color-white"
      height="height-40-percentage"
      data-cy="requests-filter-modal"
    >
      <div className="background-white border-top-radius-default padding-default font-family-gilroy-medium user-select-none inherit-parent-height ">
        <div className="flex-justify-content-space-between flex-align-items-center font-size-medium font-color-secondary padding-default ">
          <div className="">
            FILTER
            <img
              src={filterGrey}
              className="padding-left-default"
              height="12px"
              alt="filter-icon"
            />
          </div>
          <div
            className="cursor-pointer"
            onClick={() => {
              props.setRequestsFilter("clear");
              navigate(-1);
            }}
            data-cy="CLEAR-FILTER"
          >
            Clear
          </div>
        </div>
        <div className="display-flex font-size-medium font-family-gilroy-medium text-align-left inherit-parent-height">
          <div className="border-right-1px ">
            {[
              { key: "clinics", label: "Clinic & lab", cy: "clinicFilter" },
              { key: "requests", label: "Requests", cy: "requestFilter" },
              { key: "status", label: "Status", cy: "statusFilter" }
            ].map(({ key, label, cy }) => (
              <div
                key={key}
                className={`cursor-pointer padding-default ${
                  key === "clinics" ? "width-max-content" : ""
                } ${
                  props.filters.filterType.includes(key) &&
                  "background-color-grey"
                }`}
                onClick={() => props.setRequestsFilter("filterType", key)}
                data-cy={cy}
              >
                {label}
              </div>
            ))}
          </div>
          <div className="font-color-secondary padding-left-default">
            {/* doctor names filter chips */}
            {props.filters.filterType === "clinics" && (
              <div
                style={{
                  height: "calc(100% - 30px)"
                }}
                className="display-flex flex-wrap overflow-scroll"
              >
                {isValidObject(props.clinicNames) &&
                  Object.values(props.clinicNames)?.map((clinic, index) => (
                    <div
                      data-cy={clinic.id}
                      key={index}
                      className={`padding-default border-radius-default background-white margin-default text-uppercase height-fit-to-content ${
                        props.filters.clinics.includes(clinic.id)
                          ? "background-color-grey"
                          : "box-shadow-secondary"
                      } `}
                      onClick={() => {
                        props.setRequestsFilter("clinics", clinic.id);
                      }}
                    >
                      {clinic.name ? clinic.name : clinic.id}
                    </div>
                  ))}
              </div>
            )}

            {props.filters.filterType === "requests" && (
              <div className="display-flex flex-direction-column">
                {requestTypes.map((request, index) => (
                  <div
                    data-cy={request.id}
                    key={index}
                    className={`padding-default border-radius-default background-white margin-default height-fit-to-content ${
                      props.filters.requests.includes(request.id)
                        ? "background-color-grey"
                        : "box-shadow-secondary"
                    } `}
                    onClick={() => {
                      props.setRequestsFilter("requests", request.id);
                    }}
                  >
                    {request.name}
                  </div>
                ))}
              </div>
            )}

            {props.filters.filterType === "status" && (
              <div className="display-flex flex-wrap">
                {statusTypes.map((status, index) => (
                  <div
                    data-cy={status.id}
                    key={index}
                    className={`padding-default border-radius-default background-white margin-default height-fit-to-content ${
                      props.filters.status.includes(status.id)
                        ? "background-color-grey"
                        : "box-shadow-secondary"
                    } `}
                    onClick={() => {
                      props.setRequestsFilter("status", status.id);
                    }}
                  >
                    {status.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

const FallBack = ({ isFilter }) => {
  const navigate = useNavigate();
  return (
    <div className="settings-screen-content-remaining-height overflow-y-auto flex-place-children-page-center padding-large text-align-center">
      <img src={NoConnectionsIcon} alt="no connections" />
      {isFilter ? (
        <>
          <div className="padding-top-large font-family-gilroy-medium font-color-secondary font-size-large">
            We couldn't find any matches
          </div>
          <div className="font-color-tertiary font-size-large padding-top-default">
            Try changing the filters to see more connections or requests
          </div>
          <div
            className="font-family-gilroy-medium cursor-pointer font-color-primary padding-top-default"
            onClick={() => navigate("filter")}
          >
            Change filters
          </div>
        </>
      ) : (
        <>
          <div className="padding-top-large font-family-gilroy-medium font-color-secondary font-size-large">
            No connections
          </div>
          <div className="font-color-tertiary font-size-large padding-top-default">
            We'll let you know when someone wants <br /> to connect with you
          </div>
        </>
      )}
    </div>
  );
};
